import React, {type RefObject} from 'react';

/**
 * The `ChevronDownProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ChevronDownProps {
    /**
     * A string representing the CSS class to be applied to the ChevronDownIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#6E6E77'
     */
    color1?: string;
    /**
     * A number or string representing the height of the ChevronDownIcon element.
     *
     * @default 24
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ChevronDown'
     */
    testId?: string;
    /**
     * A number or string representing the height of the ChevronDownIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `ChevronDown` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `ChevronDownProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the ChevronDownIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the ChevronDownIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the ChevronDownIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const ChevronDownComponent = <ChevronDown className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const ChevronDown = ({
    className = '',
    color1 = '#6E6E77',
    height = 24,
    ref,
    testId = 'ChevronDown',
    width = 24
}: ChevronDownProps) => (
    <svg
        ref={ref}
        aria-hidden="true"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M17.3 8.3a1 1 0 1 1 1.4 1.4l-6 6a1 1 0 0 1-1.4 0l-6-6a1 1 0 1 1 1.4-1.4l5.3 5.29z" fill={color1} />
    </svg>
);

ChevronDown.displayName = 'ChevronDown';